import { functions } from "@/main";
import { forgetAdminRead, recallAdminRead, rememberAdminRead } from "@/utils/AdminReadCache";
import {
  ClientAdministrationDetail,
  ClientAdministrationList,
  ClientAdministrationRow,
  ClientDeviceCounts,
  ClientDeviceRow,
  ClientProjectRow,
  ClientReportPage,
  ClientLiveOperation,
  ClientMiraAllClients,
  ClientStatus,
  ClientStatusAction,
  ClientStatusFlags,
  ClientTestSettings,
  ClientTestSettingsValue,
  DeleteReason,
  ClientAdministrationChangePage,
  ChangeFilterGroup,
  ClientGroup,
  ClientGroupValue,
  ClientProducts,
  ClientProductsValue,
  ClientProjectOrganisation,
  ClientSystemConfiguration,
  ClientSystemConfigurationValue,
  AssistActivationOutcome,
  ClientProjectTypes,
  ClientContractChanges,
  ClientContractData,
  ClientMasterData,
  ClientMasterDataChanges,
  ClientUserList,
  NotLiveReason,
  RecalculateClientDryingsResponse,
  ClientArchivedProjectSlice,
  PdfTemplateFieldMapping,
  PdfTemplateForm,
  PdfTemplateList,
} from "../models/ClientAdministrationRow";
import { ClientSetupOptions, ClientSetupProjectOrganisation, ClientSetupRequest, ClientSetupResult } from "../models/ClientSetup";
import {
  ClientPartner,
  ClientPartnerList,
  ClientPartnerPackage,
  ClientPartnerPackageList,
  ExternalSystemRow,
  PartnerEndpointGroup,
  PartnerPackageFile,
  PartnerPackageRecipient,
  PartnerChangeGroup,
  PartnerChanges,
  PartnerCreateRequest,
} from "../models/ClientPartner";

/**
 * Swap to `listClientsForAdministrationDevelopment` **locally only** to drive the development twin
 * from a dev server. Never commit the swap.
 */
const LIST_CLIENTS_CALLABLE = "listClientsForAdministration";

/** As above: `getClientForAdministrationDevelopment` locally only, never committed. */
const GET_CLIENT_CALLABLE = "getClientForAdministration";

/** As above: `getClientAdministrationChangesForAdministrationDevelopment` locally only, never committed. */
const GET_CHANGES_CALLABLE = "getClientAdministrationChangesForAdministration";

/** As above: `updateClientAdministrationDevelopment` locally only, never committed. */
const UPDATE_CLIENT_CALLABLE = "updateClientAdministration";

/** As above: `setClientLiveOperationForAdministrationDevelopment` locally only, never committed. */
const SET_LIVE_OPERATION_CALLABLE = "setClientLiveOperationForAdministration";

/** As above: `setClientMiraAllClientsForAdministrationDevelopment` locally only, never committed. */
const SET_MIRA_ALL_CLIENTS_CALLABLE = "setClientMiraAllClientsForAdministration";

/** As above: `setClientTestSettingsForAdministrationDevelopment` locally only, never committed. */
const SET_TEST_SETTINGS_CALLABLE = "setClientTestSettingsForAdministration";

/** As above: `setClientStatusForAdministrationDevelopment` locally only, never committed. */
const SET_STATUS_CALLABLE = "setClientStatusForAdministration";

/** As above: `setClientGroupForAdministrationDevelopment` locally only, never committed. */
const SET_GROUP_CALLABLE = "setClientGroupForAdministration";

/** As above: `setClientProductsForAdministrationDevelopment` locally only, never committed. */
const SET_PRODUCTS_CALLABLE = "setClientProductsForAdministration";

/** As above: `setClientProjectTypesForAdministrationDevelopment` locally only, never committed. */
const SET_PROJECT_TYPES_CALLABLE = "setClientProjectTypesForAdministration";

/** As above: `setClientProjectOrganisationForAdministrationDevelopment` locally only, never committed. */
const SET_PROJECT_ORGANISATION_CALLABLE = "setClientProjectOrganisationForAdministration";

/** As above: `setClientSystemConfigurationForAdministrationDevelopment` locally only, never committed. */
const SET_SYSTEM_CONFIGURATION_CALLABLE = "setClientSystemConfigurationForAdministration";

/** As above: `createClientForAdministrationDevelopment` locally only, never committed. */
const CREATE_CLIENT_CALLABLE = "createClientForAdministration";

/** As above: `listClientPartnersForAdministrationDevelopment` locally only, never committed. */
const LIST_PARTNERS_CALLABLE = "listClientPartnersForAdministration";

/** As above: `getClientPartnerForAdministrationDevelopment` locally only, never committed. */
const GET_PARTNER_CALLABLE = "getClientPartnerForAdministration";

/** As above: `updateClientPartnerForAdministrationDevelopment` locally only, never committed. */
const UPDATE_PARTNER_CALLABLE = "updateClientPartnerForAdministration";

/** As above: `setClientPartnerCredentialsForAdministrationDevelopment` locally only, never committed. */
const SET_PARTNER_CREDENTIALS_CALLABLE = "setClientPartnerCredentialsForAdministration";

/** As above: `createClientPartnerForAdministrationDevelopment` locally only, never committed. */
const CREATE_PARTNER_CALLABLE = "createClientPartnerForAdministration";

/** As above: `resetClientPartnerPasswordForAdministrationDevelopment` locally only, never committed. */
const RESET_PARTNER_PASSWORD_CALLABLE = "resetClientPartnerPasswordForAdministration";

/** As above: `setClientPartnerLinkForAdministrationDevelopment` locally only, never committed. */
const SET_PARTNER_LINK_CALLABLE = "setClientPartnerLinkForAdministration";

/** As above: `listExternalSystemsForAdministrationDevelopment` locally only, never committed. */
const LIST_EXTERNAL_SYSTEMS_CALLABLE = "listExternalSystemsForAdministration";

/** As above: `listClientPartnerPackagesForAdministrationDevelopment` locally only, never committed. */
const LIST_PARTNER_PACKAGES_CALLABLE = "listClientPartnerPackagesForAdministration";

/** As above: `createClientPartnerPackageForAdministrationDevelopment` locally only, never committed. */
const CREATE_PARTNER_PACKAGE_CALLABLE = "createClientPartnerPackageForAdministration";

/** As above: `getClientPartnerPackageFileForAdministrationDevelopment` locally only, never committed. */
const GET_PARTNER_PACKAGE_FILE_CALLABLE = "getClientPartnerPackageFileForAdministration";

/** As above: `getClientSetupOptionsForAdministrationDevelopment` locally only, never committed. */
const GET_CLIENT_SETUP_OPTIONS_CALLABLE = "getClientSetupOptionsForAdministration";

/** As above: `migrateSharedUsersForAdministrationDevelopment` locally only, never committed. */
const MIGRATE_SHARED_USERS_CALLABLE = "migrateSharedUsersForAdministration";

/** As above: `getClientLoginTokenForAdministrationDevelopment` locally only, never committed. */
const GET_CLIENT_LOGIN_TOKEN_CALLABLE = "getClientLoginTokenForAdministration";

/** As above: `recalculateClientDryingsForAdministrationDevelopment` locally only, never committed. */
const RECALCULATE_DRYINGS_CALLABLE = "recalculateClientDryingsForAdministration";

/** As above: `getClientDeviceCountsForAdministrationDevelopment` locally only, never committed. */
const GET_DEVICE_COUNTS_CALLABLE = "getClientDeviceCountsForAdministration";

/** As above: `getClientDevicesForAdministrationDevelopment` locally only, never committed. */
const GET_DEVICES_CALLABLE = "getClientDevicesForAdministration";

/** As above: `getClientProjectsForAdministrationDevelopment` locally only, never committed. */
const GET_PROJECTS_CALLABLE = "getClientProjectsForAdministration";

/** As above: `getClientReportsForAdministrationDevelopment` locally only, never committed. */
const GET_REPORTS_CALLABLE = "getClientReportsForAdministration";

/** As above: `getClientUsersForAdministrationDevelopment` locally only, never committed. */
const GET_USERS_CALLABLE = "getClientUsersForAdministration";

/** As above: `getClientArchivedProjectsForAdministrationDevelopment` locally only, never committed. */
const GET_ARCHIVED_PROJECTS_CALLABLE = "getClientArchivedProjectsForAdministration";

/** As above: `getClientReportFileForAdministrationDevelopment` locally only, never committed. */
const GET_REPORT_FILE_CALLABLE = "getClientReportFileForAdministration";

/** As above: `getClientPdfTemplatesForAdministrationDevelopment` locally only, never committed. */
const GET_PDF_TEMPLATES_CALLABLE = "getClientPdfTemplatesForAdministration";

/** As above: `getClientPdfTemplateFileForAdministrationDevelopment` locally only, never committed. */
const GET_PDF_TEMPLATE_FILE_CALLABLE = "getClientPdfTemplateFileForAdministration";

/** As above: `getClientPdfTemplateFormForAdministrationDevelopment` locally only, never committed. */
const GET_PDF_TEMPLATE_FORM_CALLABLE = "getClientPdfTemplateFormForAdministration";

/** As above: `saveClientPdfTemplateMappingForAdministrationDevelopment` locally only, never committed. */
const SAVE_PDF_TEMPLATE_MAPPING_CALLABLE = "saveClientPdfTemplateMappingForAdministration";

/** As above: each of these has a `…Development` twin, swapped in locally only, never committed. */
const UPLOAD_PDF_TEMPLATE_CALLABLE = "uploadClientPdfTemplateForAdministration";
const REPLACE_PDF_TEMPLATE_FILE_CALLABLE = "replaceClientPdfTemplateFileForAdministration";
const RENAME_PDF_TEMPLATE_CALLABLE = "renameClientPdfTemplateForAdministration";
const COPY_PDF_TEMPLATE_CALLABLE = "copyClientPdfTemplateForAdministration";
const DELETE_PDF_TEMPLATE_CALLABLE = "deleteClientPdfTemplateForAdministration";
const PREVIEW_PDF_TEMPLATE_CALLABLE = "previewClientPdfTemplateForAdministration";

/**
 * Every client, for the admin portal's clients list, deleted and inactive ones included.
 *
 * Served by a Cloud callable rather than read from Firestore, because the portal's access checks
 * run in the browser: the callable refuses unless the caller's client is a portal client and the
 * user's role grants `MANAGE_CLIENTS`, and it returns only the fields the list shows.
 *
 * @returns the rows, unsorted, and when their figures were last counted. Rejects when Cloud refuses
 *   or fails; the caller reports it.
 */
export async function listClientsForAdministration(): Promise<ClientAdministrationList> {
  const callable = functions.httpsCallable(LIST_CLIENTS_CALLABLE);
  const result = await callable();
  const data = result.data as { clients?: ClientAdministrationRow[]; figuresUpdatedAt?: unknown } | null;

  const list = {
    clients: Array.isArray(data?.clients) ? data?.clients ?? [] : [],
    figuresUpdatedAt: typeof data?.figuresUpdatedAt === "number" ? data.figuresUpdatedAt : null,
  };
  rememberAdminRead(LIST_CLIENTS_CALLABLE, null, list);
  return list;
}

/** The last answer of {@link listClientsForAdministration} in this tab, or null (`AdminReadCache.ts`). */
export function cachedClientsForAdministration(): ClientAdministrationList | null {
  return recallAdminRead(LIST_CLIENTS_CALLABLE, null);
}

/**
 * One client's detail overview, for the admin portal's client detail screen.
 *
 * Served by a Cloud callable for the same reason as the list: it re-checks the portal client and
 * `MANAGE_CLIENTS` server-side, and returns computed values only.
 *
 * @param clientId - the client to show.
 * @returns the overview. Rejects when Cloud refuses, the client does not exist, or the call fails.
 */
export async function getClientForAdministration(
  clientId: string
): Promise<ClientAdministrationDetail> {
  const callable = functions.httpsCallable(GET_CLIENT_CALLABLE);
  const result = await callable({ clientId });
  const data = result.data as { client?: ClientAdministrationDetail; figuresUpdatedAt?: unknown } | null;

  if (!data?.client) {
    throw new Error("The client detail response carried no client.");
  }

  const detail = {
    ...data.client,
    figuresUpdatedAt: typeof data.figuresUpdatedAt === "number" ? data.figuresUpdatedAt : null,
  };
  rememberAdminRead(GET_CLIENT_CALLABLE, clientId, detail);
  return detail;
}

/** The last answer of {@link getClientForAdministration} for this client in this tab, or null. */
export function cachedClientForAdministration(clientId: string): ClientAdministrationDetail | null {
  return recallAdminRead(GET_CLIENT_CALLABLE, clientId);
}

/**
 * Drops the cached detail of a client, for a change made from another client's page that its next
 * opening must not show the old state of first (a group save adding or removing it).
 */
export function forgetCachedClientForAdministration(clientId: string): void {
  forgetAdminRead(GET_CLIENT_CALLABLE, clientId);
}

/**
 * Saves changed Stammdaten of a client (backlog #38). IRES Cloud repeats the access check and every
 * rule, refuses a field changed since the dialog loaded it (`CHANGED_MEANWHILE`) or a Kürzel another
 * client holds (`PREFIX_TAKEN`), writes only the changed fields and records the change.
 *
 * @param clientId - the client.
 * @param changes - each changed field with the value the dialog loaded and the new one.
 * @returns the Stammdaten after the save. Rejects with the callable's refusal.
 */
export async function updateClientMasterData(
  clientId: string,
  changes: ClientMasterDataChanges
): Promise<ClientMasterData | null> {
  const callable = functions.httpsCallable(UPDATE_CLIENT_CALLABLE);
  const result = await callable({ clientId, group: "MASTER_DATA", changes });
  return (result.data as { masterData?: ClientMasterData } | null)?.masterData ?? null;
}

/**
 * A page of a client's Verlauf: what the IRES Admin Client changed on it, newest first, 20 at a time.
 * The rows are unreadable from the browser; IRES Cloud repeats the access check.
 *
 * @param clientId - the client.
 * @param startAfterId - the page before's cursor, or null for the first page.
 * @param excludeGroups - the groups to leave out (#2610071); IRES Cloud skips them, so a page stays full.
 * @returns the page. Rejects when Cloud refuses or the call fails.
 */
export async function getClientAdministrationChanges(
  clientId: string,
  startAfterId: string | null,
  excludeGroups: ChangeFilterGroup[] = []
): Promise<ClientAdministrationChangePage> {
  const callable = functions.httpsCallable(GET_CHANGES_CALLABLE);
  const result = await callable({ clientId, startAfterId, excludeGroups });
  const data = result.data as Partial<ClientAdministrationChangePage> | null;
  const changes = data?.changes;
  return {
    changes: Array.isArray(changes) ? changes : [],
    nextCursor: typeof data?.nextCursor === "string" ? data.nextCursor : null,
  };
}

/**
 * Saves changed Verträge of a client (backlog #38, slice 2), through the same callable as the
 * Stammdaten. IRES Cloud clears a start date whose contract is unticked and refuses a change made
 * meanwhile (`CHANGED_MEANWHILE`).
 *
 * @param clientId - the client.
 * @param changes - each changed field with the value the dialog loaded and the new one.
 * @returns the Verträge after the save. Rejects with the callable's refusal.
 */
export async function updateClientContracts(
  clientId: string,
  changes: ClientContractChanges
): Promise<ClientContractData | null> {
  const callable = functions.httpsCallable(UPDATE_CLIENT_CALLABLE);
  const result = await callable({ clientId, group: "CONTRACTS", changes });
  return (result.data as { contracts?: ClientContractData } | null)?.contracts ?? null;
}

/**
 * Marks a client "Kein Echtbetrieb" with a reason, or live operation again (ticket 2610043). IRES Cloud
 * repeats the access check and the validation, and records who changed it and when.
 *
 * @param clientId - the client.
 * @param reason - why it is not live operation, or null to mark it live again.
 * @returns the stored marker. Rejects when Cloud refuses or the call fails.
 */
export async function setClientLiveOperation(
  clientId: string,
  reason: NotLiveReason | null
): Promise<ClientLiveOperation | null> {
  const callable = functions.httpsCallable(SET_LIVE_OPERATION_CALLABLE);
  const result = await callable({ clientId, live: reason === null, reason });
  return (result.data as { liveOperation?: ClientLiveOperation | null } | null)?.liveOperation ?? null;
}

/**
 * Switches "Mira: Alle Mandanten" on an IRES account, or off (#2610111). IRES Cloud repeats the access
 * check, refuses it on any account that is not IRES's own (`NOT_IRES_ACCOUNT`), records who changed it,
 * and removes the combined figures at once when it goes off.
 *
 * @param clientId - the account.
 * @param enabled - on or off.
 * @returns the stored switch. Rejects when Cloud refuses or the call fails.
 */
export async function setClientMiraAllClients(clientId: string, enabled: boolean): Promise<ClientMiraAllClients | null> {
  const callable = functions.httpsCallable(SET_MIRA_ALL_CLIENTS_CALLABLE);
  const result = await callable({ clientId, enabled });
  return (result.data as { miraAllClients?: ClientMiraAllClients | null } | null)?.miraAllClients ?? null;
}

/**
 * Changes a client's test settings from the Test card (#2610070): the ERP-Simulator and the Test-App
 * targets. IRES Cloud checks access, refuses a stale view (`CHANGED_MEANWHILE`) or a test user who is
 * not an active user of the client (`UNKNOWN_TEST_USER`), and records a Verlauf row.
 *
 * @param clientId - the client.
 * @param expected - the settings as the dialog showed them.
 * @param value - the settings to store.
 * @returns the stored settings. Rejects when Cloud refuses or the call fails.
 */
export async function setClientTestSettings(
  clientId: string,
  expected: ClientTestSettingsValue,
  value: ClientTestSettingsValue
): Promise<ClientTestSettings | null> {
  const callable = functions.httpsCallable(SET_TEST_SETTINGS_CALLABLE);
  const result = await callable({ clientId, expected, value });
  return (result.data as { testSettings?: ClientTestSettings | null } | null)?.testSettings ?? null;
}

/**
 * Saves a client's group from the Gruppe card (#2610074): MASTER-Mandant or not, its members, the member
 * whose settings it takes over. IRES Cloud checks access, refuses a stale view (`CHANGED_MEANWHILE`) and
 * whatever is not allowed (`details.reason`), writes every part in one go and records it in the Verlauf
 * of the master and of each member added or removed.
 *
 * @param clientId - the client.
 * @param expected - the group as the card shows it.
 * @param value - the group to save.
 * @returns the group after the save. Rejects when Cloud refuses or the call fails.
 */
export async function setClientGroup(
  clientId: string,
  expected: ClientGroupValue,
  value: ClientGroupValue
): Promise<ClientGroup | null> {
  const callable = functions.httpsCallable(SET_GROUP_CALLABLE);
  const result = await callable({ clientId, expected, value });
  return (result.data as { group?: ClientGroup | null } | null)?.group ?? null;
}

/**
 * Saves a client's products from the Produkte card (#2610089 stage 1): the four switches (GZ / GL in IRES
 * Office, IRES Project, IRES Work, Schnittstelle). IRES Cloud checks access, refuses a stale view
 * (`CHANGED_MEANWHILE`) and whatever is not allowed (`details.reason`), writes the switches and the derived
 * `projectManagement`, recalculates the licences and records a Verlauf row.
 *
 * @param clientId - the client.
 * @param expected - the switches as the card shows them.
 * @param value - the switches to save.
 * @returns the products after the save. Rejects when Cloud refuses or the call fails.
 */
export async function setClientProducts(
  clientId: string,
  expected: ClientProductsValue,
  value: ClientProductsValue
): Promise<ClientProducts | null> {
  const callable = functions.httpsCallable(SET_PRODUCTS_CALLABLE);
  const result = await callable({ clientId, expected, value });
  return (result.data as { products?: ClientProducts | null } | null)?.products ?? null;
}

/**
 * Sets which project types IRES gives a client (#2610081), with the list the dialog loaded as `expected`
 * so IRES Cloud can refuse a change made meanwhile. A type taken away is switched off in the client's
 * own table by IRES Cloud in the same save.
 *
 * @param clientId - the client.
 * @param expected - the type ids the client got when the dialog opened.
 * @param value - the type ids it gets after the save.
 * @returns the project types as the detail serves them after the save, or null when IRES Cloud sent none.
 *   Rejects with IRES Cloud's `details.reason` when refused.
 */
export async function setClientProjectTypes(
  clientId: string,
  expected: number[],
  value: number[]
): Promise<ClientProjectTypes | null> {
  const callable = functions.httpsCallable(SET_PROJECT_TYPES_CALLABLE);
  const result = await callable({ clientId, expected, value });
  return (result.data as { projectTypes?: ClientProjectTypes | null } | null)?.projectTypes ?? null;
}

/**
 * Sets a client's Projektorganisation from the IRES Admin Client's Projektorganisation card, sending the
 * answers the dialog loaded with the new ones, so IRES Cloud can refuse a change made meanwhile.
 *
 * @param clientId - the client.
 * @param expected - the four answers when the dialog opened.
 * @param value - the four answers after the save.
 * @returns the Projektorganisation as the detail serves it after the save, or null when IRES Cloud sent
 *   none. Rejects with IRES Cloud's `details.reason` when refused.
 */
export async function setClientProjectOrganisation(
  clientId: string,
  expected: ClientSetupProjectOrganisation,
  value: ClientSetupProjectOrganisation
): Promise<ClientProjectOrganisation | null> {
  const callable = functions.httpsCallable(SET_PROJECT_ORGANISATION_CALLABLE);
  const result = await callable({ clientId, expected, value });
  return (result.data as { projectOrganisation?: ClientProjectOrganisation | null } | null)?.projectOrganisation ?? null;
}

/** What a Systemkonfiguration save answers: the values after it, how ASSIST's activation went, and whether a side effect failed. */
export interface ClientSystemConfigurationSaveResult {
  systemConfiguration: ClientSystemConfiguration | null;
  assistActivation: AssistActivationOutcome;
  effectsFailed: boolean;
}

/**
 * Sets a client's Systemkonfiguration from the IRES Admin Client's Systemkonfiguration card (#2610141),
 * sending the values the dialog loaded with the new ones, so IRES Cloud can refuse a change made meanwhile.
 * IRES Cloud runs what the change sets off (vehicles, Adressen geocodieren, ASSIST's activation).
 *
 * @param clientId - the client.
 * @param expected - the values when the dialog opened.
 * @param value - the values after the save.
 * @returns what IRES Cloud answered. Rejects with IRES Cloud's `details.reason` when refused.
 */
export async function setClientSystemConfiguration(
  clientId: string,
  expected: ClientSystemConfigurationValue,
  value: ClientSystemConfigurationValue
): Promise<ClientSystemConfigurationSaveResult> {
  const callable = functions.httpsCallable(SET_SYSTEM_CONFIGURATION_CALLABLE);
  const result = await callable({ clientId, expected, value });
  const data = result.data as Partial<ClientSystemConfigurationSaveResult> | null;
  return {
    systemConfiguration: data?.systemConfiguration ?? null,
    assistActivation: data?.assistActivation ?? null,
    effectsFailed: data?.effectsFailed === true,
  };
}

/**
 * What the guided setup offers (#2610082): the project types each product combination allows, and the
 * MASTER-Mandanten a new client can join. With `masterClientId`, that master comes with the settings a
 * member follows.
 *
 * @param masterClientId - the master whose settings to include, or null.
 * @returns the options. Rejects when Cloud refuses or the call fails.
 */
export async function getClientSetupOptions(masterClientId: string | null): Promise<ClientSetupOptions> {
  const callable = functions.httpsCallable(GET_CLIENT_SETUP_OPTIONS_CALLABLE);
  const result = await callable(masterClientId === null ? {} : { masterClientId });
  const data = (result.data ?? {}) as Partial<ClientSetupOptions>;
  return { possibleProjectTypeIds: data.possibleProjectTypeIds ?? {}, masters: data.masters ?? [], fleetTargets: data.fleetTargets ?? {} };
}

/**
 * Creates a client from the guided setup (#2610082): the sign-in account and every document, all or
 * nothing. IRES Cloud checks access and every rule again, and refuses with `details.reason`. With an
 * external system (#2610084, merge 4), its id and password come back, the password once.
 *
 * @param request - the setup.
 * @returns the new client's id, and the new system's id and password or null. Rejects when Cloud refuses or the call fails.
 */
export async function createClient(request: ClientSetupRequest): Promise<ClientSetupResult> {
  const callable = functions.httpsCallable(CREATE_CLIENT_CALLABLE);
  const result = await callable(request);
  const data = (result.data ?? {}) as { clientId?: unknown; externalSystem?: { id?: unknown; password?: unknown } | null };
  if (typeof data.clientId !== "string" || data.clientId === "") {
    throw new Error("No client id returned.");
  }
  const system = data.externalSystem;
  return {
    clientId: data.clientId,
    externalSystem: system && typeof system.id === "string" && typeof system.password === "string" ? { id: system.id, password: system.password } : null,
  };
}

/**
 * "Geteilte Benutzer migrieren" for a MASTER-Mandant (#2610074): the shared email addresses of its group
 * become shared users of the group.
 *
 * @param clientId - the MASTER-Mandant.
 * @returns how many were migrated. Rejects when Cloud refuses (`NOT_A_MASTER`) or the call fails.
 */
export async function migrateSharedUsersOfGroup(clientId: string): Promise<number> {
  const callable = functions.httpsCallable(MIGRATE_SHARED_USERS_CALLABLE);
  const result = await callable({ clientId });
  const migrated = (result.data as { migrated?: unknown } | null)?.migrated;
  return typeof migrated === "number" ? migrated : 0;
}

/**
 * Changes one flag on a client's Status card (#2610067): Gesperrt, Aktiv / Inaktiv, Gelöscht. IRES Cloud
 * checks access, refuses a stale view (`CHANGED_MEANWHILE`) and, for deleting or deactivating, the
 * conditions (`CONDITIONS_NOT_MET` with the list), and records who changed it and a Verlauf row.
 *
 * @param clientId - the client.
 * @param action - the change.
 * @param expected - the flags as the screen shows them.
 * @param reason - why it is deleted (DELETE only).
 * @param note - the optional note (BLOCK, DELETE; required with OTHER).
 * @returns the Status after the change. Rejects when Cloud refuses or the call fails.
 */
export async function setClientStatus(
  clientId: string,
  action: ClientStatusAction,
  expected: ClientStatusFlags,
  reason: DeleteReason | null,
  note: string | null
): Promise<ClientStatus | null> {
  const callable = functions.httpsCallable(SET_STATUS_CALLABLE);
  const result = await callable({ clientId, action, expected, reason, note });
  const data = result.data as ClientStatus | null;
  return data?.status ? data : null;
}

/**
 * Starts a fresh ASSIST recalculation of every drying of a client, from the Hilfsfunktionen card on the
 * client detail (#2610077). IRES Cloud repeats the access check, records the run in the client's Verlauf,
 * and refuses by `details.reason` (`INVALID`, `NO_CLIENT`, `DELETED_CLIENT`, `SHARED_LOGIN`, or
 * `ASSIST_FAILED` with `details.apiMessage`) or with `permission-denied` for a missing `MANAGE_CLIENTS`.
 *
 * @param clientId - the client whose dryings to recalculate.
 * @returns the start status and the job id (null when nothing was started). Rejects with the refusal.
 */
export async function recalculateClientDryings(clientId: string): Promise<RecalculateClientDryingsResponse> {
  const callable = functions.httpsCallable(RECALCULATE_DRYINGS_CALLABLE);
  const result = await callable({ clientId });
  const data = result.data as Partial<RecalculateClientDryingsResponse> | null;
  const status = data?.status;

  return {
    status: status === "already_running" || status === "no_op" ? status : "started",
    jobId: typeof data?.jobId === "string" ? data.jobId : null,
  };
}

/**
 * The custom token that signs a new tab in to a client as its IRES admin user ("Als Mandant anmelden",
 * #62). IRES Cloud checks access and the client, and records the opening in the client's Verlauf. The
 * token goes only to the new tab (`ClientLoginHandover.ts`), never into a URL, storage or a log.
 *
 * @param clientId - the client to open.
 * @returns the token. Rejects when Cloud refuses (`details.reason`) or the call fails.
 */
export async function getClientLoginToken(clientId: string): Promise<string> {
  const callable = functions.httpsCallable(GET_CLIENT_LOGIN_TOKEN_CALLABLE);
  const result = await callable({ clientId });
  const token = (result.data as { token?: unknown } | null)?.token;
  if (typeof token !== "string" || token === "") {
    throw new Error("No token");
  }
  return token;
}

/**
 * The client's panel, ComBox and Mini counts per assignment type, for the client detail screen.
 *
 * A separate call from the overview because it reads every assignment of the client plus one
 * document per device, so the overview does not wait for it. Server-checked like the others.
 *
 * @param clientId - the client to count.
 * @returns the counts. Rejects when Cloud refuses or the call fails.
 */
export async function getClientDeviceCountsForAdministration(
  clientId: string
): Promise<ClientDeviceCounts> {
  const callable = functions.httpsCallable(GET_DEVICE_COUNTS_CALLABLE);
  const result = await callable({ clientId });
  const counts = (result.data as { counts?: ClientDeviceCounts } | null)?.counts;

  if (!counts) {
    throw new Error("The device counts response carried no counts.");
  }

  rememberAdminRead(GET_DEVICE_COUNTS_CALLABLE, clientId, counts);
  return counts;
}

/** The last answer of {@link getClientDeviceCountsForAdministration} for this client in this tab, or null. */
export function cachedClientDeviceCountsForAdministration(clientId: string): ClientDeviceCounts | null {
  return recallAdminRead(GET_DEVICE_COUNTS_CALLABLE, clientId);
}

/**
 * Every device the client has or had, for the device list behind the Geräte card. Server-checked like
 * the others; each row carries the counts' own classification.
 *
 * @param clientId - the client.
 * @returns the rows, by serial number. Rejects when Cloud refuses or the call fails.
 */
export async function getClientDevicesForAdministration(clientId: string): Promise<ClientDeviceRow[]> {
  const callable = functions.httpsCallable(GET_DEVICES_CALLABLE);
  const result = await callable({ clientId });
  const devices = (result.data as { devices?: ClientDeviceRow[] } | null)?.devices;
  const rows = Array.isArray(devices) ? devices : [];

  rememberAdminRead(GET_DEVICES_CALLABLE, clientId, rows);
  return rows;
}

/** The last answer of {@link getClientDevicesForAdministration} for this client in this tab, or null. */
export function cachedClientDevicesForAdministration(clientId: string): ClientDeviceRow[] | null {
  return recallAdminRead(GET_DEVICES_CALLABLE, clientId);
}

/**
 * The client's running and finished projects, for the project list behind the detail's Projekte
 * count. Server-checked like the others; the callable returns no personal data (contacts, addresses,
 * staff), only what the list shows. The archive (Alte Projekte) is not included.
 *
 * @param clientId - the client.
 * @returns the rows, newest start first. Rejects when Cloud refuses or the call fails.
 */
export async function getClientProjectsForAdministration(clientId: string): Promise<ClientProjectRow[]> {
  const callable = functions.httpsCallable(GET_PROJECTS_CALLABLE);
  const result = await callable({ clientId });
  const projects = (result.data as { projects?: ClientProjectRow[] } | null)?.projects;
  const rows = Array.isArray(projects) ? projects : [];

  rememberAdminRead(GET_PROJECTS_CALLABLE, clientId, rows);
  return rows;
}

/** The last answer of {@link getClientProjectsForAdministration} for this client in this tab, or null. */
export function cachedClientProjectsForAdministration(clientId: string): ClientProjectRow[] | null {
  return recallAdminRead(GET_PROJECTS_CALLABLE, clientId);
}

/**
 * One page of the client's reports, newest first, for the report list: the whole client's, or with
 * `projectId` one project's. Server-checked like the others, and metadata only (no PDF, no storage
 * path, no staff). Paged on the server: the largest client has about 112,000 reports.
 *
 * @param clientId - the client.
 * @param projectId - one of its projects, or null for all.
 * @param startAfterId - the previous page's `nextCursor`, or null for the first page.
 * @returns the page. Rejects when Cloud refuses or the call fails.
 */
export async function getClientReportsForAdministration(
  clientId: string,
  projectId: string | null,
  startAfterId: string | null
): Promise<ClientReportPage> {
  const callable = functions.httpsCallable(GET_REPORTS_CALLABLE);
  const result = await callable({
    clientId,
    ...(projectId ? { projectId } : {}),
    ...(startAfterId ? { startAfterId } : {}),
  });
  const page = result.data as Partial<ClientReportPage> | null;
  const reports = page?.reports;

  const answer = {
    reports: Array.isArray(reports) ? reports : [],
    nextCursor: typeof page?.nextCursor === "string" ? page.nextCursor : null,
    total: typeof page?.total === "number" ? page.total : null,
  };
  // The first page only: a later one is asked for on purpose, and a cursor goes stale with new reports.
  if (startAfterId === null) {
    rememberAdminRead(GET_REPORTS_CALLABLE, { clientId, projectId }, answer);
  }
  return answer;
}

/** The last first page of {@link getClientReportsForAdministration} for this client and project, or null. */
export function cachedClientReportsForAdministration(clientId: string, projectId: string | null): ClientReportPage | null {
  return recallAdminRead(GET_REPORTS_CALLABLE, { clientId, projectId });
}

/**
 * The client's users, deleted ones included and flagged, and the role names the client runs on, for
 * the user list behind the detail's Benutzer count. Server-checked like the others, with the IRES
 * Admin fields only: no contact data, address, staff number, password data or session token.
 *
 * @param clientId - the client.
 * @returns the users, unsorted, and the role labels. Rejects when Cloud refuses or the call fails.
 */
export async function getClientUsersForAdministration(clientId: string): Promise<ClientUserList> {
  const callable = functions.httpsCallable(GET_USERS_CALLABLE);
  const result = await callable({ clientId });
  const data = result.data as Partial<ClientUserList> | null;
  const users = data?.users;
  const roleLabels = data?.roleLabels;

  const list = {
    users: Array.isArray(users) ? users : [],
    roleLabels: roleLabels && typeof roleLabels === "object" ? roleLabels : {},
  };
  rememberAdminRead(GET_USERS_CALLABLE, clientId, list);
  return list;
}

/** The last answer of {@link getClientUsersForAdministration} for this client in this tab, or null. */
export function cachedClientUsersForAdministration(clientId: string): ClientUserList | null {
  return recallAdminRead(GET_USERS_CALLABLE, clientId);
}

/**
 * One slice of the client's archived projects (Alte Projekte, deleted ones flagged), for the project
 * list when its filter asks for the archive. Server-checked like the others, with the same rows as
 * the running and finished list. A slice is 20 page documents; the first call also counts the pages.
 *
 * @param clientId - the client.
 * @param startAfterPageId - the previous slice's `nextCursor`, or null for the first slice.
 * @returns the slice. Rejects when Cloud refuses or the call fails.
 */
export async function getClientArchivedProjectsForAdministration(
  clientId: string,
  startAfterPageId: string | null
): Promise<ClientArchivedProjectSlice> {
  const callable = functions.httpsCallable(GET_ARCHIVED_PROJECTS_CALLABLE);
  const result = await callable({ clientId, ...(startAfterPageId ? { startAfterPageId } : {}) });
  const slice = result.data as Partial<ClientArchivedProjectSlice> | null;
  const projects = slice?.projects;

  return {
    projects: Array.isArray(projects) ? projects : [],
    nextCursor: typeof slice?.nextCursor === "string" ? slice.nextCursor : null,
    pageCount: typeof slice?.pageCount === "number" ? slice.pageCount : null,
  };
}

/**
 * A five-minute link to one report's file, for opening it from the report list. IRES Cloud reads the
 * file from the report itself (only ids are sent), serves it only from that client's own storage
 * area, and logs the opening.
 *
 * @param clientId - the client.
 * @param reportId - the report.
 * @returns the signed link. Rejects with `not-found` (no report or no file), `failed-precondition`
 *   (the report has no file of its own client) or a refusal.
 */
export async function getClientReportFileForAdministration(clientId: string, reportId: string): Promise<string> {
  const callable = functions.httpsCallable(GET_REPORT_FILE_CALLABLE);
  const result = await callable({ clientId, reportId });
  const url = (result.data as { url?: unknown } | null)?.url;

  if (typeof url !== "string" || url === "") {
    throw new Error("The report file response carried no link.");
  }

  return url;
}

/**
 * A client's PDF templates, or the shared ones (`clientId` null), with each field's mapping and what
 * IRES Cloud does with every mapped value, and which template the HyDry certificate uses.
 * Server-checked like the others; no storage path is returned.
 *
 * @param clientId - the client, or null for the shared templates.
 * @returns the templates. Rejects when Cloud refuses or the call fails.
 */
export async function getClientPdfTemplatesForAdministration(clientId: string | null): Promise<PdfTemplateList> {
  const callable = functions.httpsCallable(GET_PDF_TEMPLATES_CALLABLE);
  const result = await callable({ clientId });
  const data = result.data as Partial<PdfTemplateList> | null;
  const templates = data?.templates;

  return {
    templates: Array.isArray(templates) ? templates : [],
    hyDryCertificateTemplateId: typeof data?.hyDryCertificateTemplateId === "string" ? data.hyDryCertificateTemplateId : null,
  };
}

/**
 * A five-minute download link to a template's PDF; IRES Cloud serves it only from the scope's own
 * folder and logs the download.
 *
 * @param clientId - the client, or null for a shared template.
 * @param templateId - the template.
 * @returns the signed link. Rejects with `not-found`, `failed-precondition` or a refusal.
 */
export async function getClientPdfTemplateFileForAdministration(clientId: string | null, templateId: string): Promise<string> {
  const callable = functions.httpsCallable(GET_PDF_TEMPLATE_FILE_CALLABLE);
  const result = await callable({ clientId, templateId });
  const url = (result.data as { url?: unknown } | null)?.url;

  if (typeof url !== "string" || url === "") {
    throw new Error("The template file response carried no link.");
  }

  return url;
}

/**
 * One template for the mapping editor: the template, its PDF's form fields (or why they could not be
 * read) and IRES Admin's value catalogue. Server-checked like the others.
 *
 * @param clientId - the client, or null for a shared template.
 * @param templateId - the template.
 * @returns the template and what the editor needs. Rejects with `not-found` or a refusal.
 */
export async function getClientPdfTemplateFormForAdministration(
  clientId: string | null,
  templateId: string
): Promise<PdfTemplateForm> {
  const callable = functions.httpsCallable(GET_PDF_TEMPLATE_FORM_CALLABLE);
  const result = await callable({ clientId, templateId });
  const form = result.data as PdfTemplateForm | null;

  if (!form?.template) {
    throw new Error("The template form response carried no template.");
  }

  return {
    template: form.template,
    isHyDryCertificateTemplate: form.isHyDryCertificateTemplate === true,
    formFields: Array.isArray(form.formFields) ? form.formFields : null,
    formFieldsUnavailable: form.formFieldsUnavailable ?? null,
    catalogue: Array.isArray(form.catalogue) ? form.catalogue : [],
  };
}

/**
 * Saves a template's field mapping. IRES Cloud refuses with `aborted` when the template was saved
 * since `expectedLastSavedMillis` (IRES Admin still saves templates whole), and with `invalid-argument`
 * plus a reason when a mapping does not fit the PDF or the catalogue.
 *
 * @param clientId - the client, or null for a shared template.
 * @param templateId - the template.
 * @param expectedLastSavedMillis - the loaded template's `lastSavedMillis`.
 * @param fields - every mapped field, in the order to store them; a field without values is dropped.
 * @returns the new last-saved time.
 */
export async function saveClientPdfTemplateMappingForAdministration(
  clientId: string | null,
  templateId: string,
  expectedLastSavedMillis: number | null,
  fields: PdfTemplateFieldMapping[]
): Promise<number> {
  const callable = functions.httpsCallable(SAVE_PDF_TEMPLATE_MAPPING_CALLABLE);
  const result = await callable({ clientId, templateId, expectedLastSavedMillis, fields });
  const lastSavedMillis = (result.data as { lastSavedMillis?: unknown } | null)?.lastSavedMillis;

  if (typeof lastSavedMillis !== "number") {
    throw new Error("The save response carried no save time.");
  }

  return lastSavedMillis;
}

/** The callable's `{ templateId }`, or a failure when it carried none. */
function templateIdOf(data: unknown): string {
  const templateId = (data as { templateId?: unknown } | null)?.templateId;
  if (typeof templateId !== "string" || templateId === "") {
    throw new Error("The response carried no template id.");
  }
  return templateId;
}

/** The callable's `{ lastSavedMillis }`, or a failure when it carried none. */
function lastSavedOf(data: unknown): number {
  const lastSavedMillis = (data as { lastSavedMillis?: unknown } | null)?.lastSavedMillis;
  if (typeof lastSavedMillis !== "number") {
    throw new Error("The response carried no save time.");
  }
  return lastSavedMillis;
}

/**
 * A new template from a PDF, under a file name never used in the scope (IRES Project caches template
 * PDFs by name). Rejects with `already-exists` (`FILE_NAME_TAKEN`) or `invalid-argument` and a reason.
 *
 * @param clientId - the client, or null for a shared template.
 * @param fileName - the name; `.pdf` is added when missing.
 * @param contentBase64 - the PDF, at most 6 MB.
 * @returns the new template's id.
 */
export async function uploadClientPdfTemplateForAdministration(
  clientId: string | null,
  fileName: string,
  contentBase64: string
): Promise<string> {
  const result = await functions.httpsCallable(UPLOAD_PDF_TEMPLATE_CALLABLE)({ clientId, fileName, contentBase64 });
  return templateIdOf(result.data);
}

/**
 * A template's PDF replaced by a new file under a new, free name; the mapping and the old file stay.
 * Rejects with `aborted` when the template was saved since `expectedLastSavedMillis`.
 *
 * @returns the new last-saved time.
 */
export async function replaceClientPdfTemplateFileForAdministration(
  clientId: string | null,
  templateId: string,
  expectedLastSavedMillis: number | null,
  fileName: string,
  contentBase64: string
): Promise<number> {
  const result = await functions.httpsCallable(REPLACE_PDF_TEMPLATE_FILE_CALLABLE)({
    clientId,
    templateId,
    expectedLastSavedMillis,
    fileName,
    contentBase64,
  });
  return lastSavedOf(result.data);
}

/**
 * A template renamed to a name free in its scope; the file stays. Rejects with `aborted` when the
 * template was saved since `expectedLastSavedMillis`.
 *
 * @returns the new last-saved time.
 */
export async function renameClientPdfTemplateForAdministration(
  clientId: string | null,
  templateId: string,
  expectedLastSavedMillis: number | null,
  fileName: string
): Promise<number> {
  const result = await functions.httpsCallable(RENAME_PDF_TEMPLATE_CALLABLE)({
    clientId,
    templateId,
    expectedLastSavedMillis,
    fileName,
  });
  return lastSavedOf(result.data);
}

/**
 * A template copied between the shared templates and a client (one of the two ids null), file and
 * mapping, under a name free in the target scope.
 *
 * @returns the copy's id.
 */
export async function copyClientPdfTemplateForAdministration(
  fromClientId: string | null,
  templateId: string,
  toClientId: string | null,
  fileName: string
): Promise<string> {
  const result = await functions.httpsCallable(COPY_PDF_TEMPLATE_CALLABLE)({ fromClientId, templateId, toClientId, fileName });
  return templateIdOf(result.data);
}

/**
 * A template's record deleted; IRES Cloud keeps its file and logs the record. Rejects with `aborted`
 * when the template was saved since `expectedLastSavedMillis`.
 */
export async function deleteClientPdfTemplateForAdministration(
  clientId: string | null,
  templateId: string,
  expectedLastSavedMillis: number | null
): Promise<void> {
  await functions.httpsCallable(DELETE_PDF_TEMPLATE_CALLABLE)({ clientId, templateId, expectedLastSavedMillis });
}

/**
 * The template's PDF with the value names filled in, from the given (unsaved) mapping or, without one,
 * the stored mapping. Nothing is stored.
 *
 * @returns the filled PDF and the template's file name.
 */
export async function previewClientPdfTemplateForAdministration(
  clientId: string | null,
  templateId: string,
  fields: PdfTemplateFieldMapping[] | null
): Promise<{ contentBase64: string; fileName: string }> {
  const result = await functions.httpsCallable(PREVIEW_PDF_TEMPLATE_CALLABLE)({ clientId, templateId, fields });
  const data = result.data as { contentBase64?: unknown; fileName?: unknown } | null;

  if (typeof data?.contentBase64 !== "string") {
    throw new Error("The preview response carried no PDF.");
  }
  return { contentBase64: data.contentBase64, fileName: typeof data.fileName === "string" ? data.fileName : "Vorlage.pdf" };
}

/**
 * The Externe Systeme card (#2610084): the client's own partners, and for a member its master's it is
 * linked to (read-only).
 *
 * @param clientId - the client.
 * @returns the partners, by name. Rejects when Cloud refuses or the call fails.
 */
export async function listClientPartners(clientId: string): Promise<ClientPartnerList> {
  const callable = functions.httpsCallable(LIST_PARTNERS_CALLABLE);
  const result = await callable({ clientId });
  const data = (result.data ?? {}) as Partial<ClientPartnerList>;
  return { partners: data.partners ?? [], mayCreate: data.mayCreate === true };
}

/**
 * One partner's page (#2610084); a secret only as set or missing.
 *
 * @param clientId - the client whose detail opened it.
 * @param partnerId - the partner.
 */
export async function getClientPartner(clientId: string, partnerId: string): Promise<ClientPartner> {
  const callable = functions.httpsCallable(GET_PARTNER_CALLABLE);
  const result = await callable({ clientId, partnerId });
  const partner = (result.data as { partner?: ClientPartner } | null)?.partner;
  if (!partner) {
    throw new Error("No partner returned.");
  }
  return partner;
}

/**
 * One card's change on a partner (#2610084), each field with the value the dialog showed. IRES Cloud
 * refuses a change made meanwhile (`CHANGED_MEANWHILE`) and records the change in the Verlauf.
 *
 * @returns the page after the change.
 */
export async function updateClientPartner(
  clientId: string,
  partnerId: string,
  group: PartnerChangeGroup,
  changes: PartnerChanges
): Promise<ClientPartner> {
  const callable = functions.httpsCallable(UPDATE_PARTNER_CALLABLE);
  const result = await callable({ clientId, partnerId, group, changes });
  const partner = (result.data as { partner?: ClientPartner } | null)?.partner;
  if (!partner) {
    throw new Error("No partner returned.");
  }
  return partner;
}

/**
 * Replaces credentials IRES uses to call a partner (#2610084). Write-only: nothing is read back.
 *
 * @param credentials - the fields to replace, each a non-empty text.
 * @returns the page after the change.
 */
export async function setClientPartnerCredentials(
  clientId: string,
  partnerId: string,
  credentials: Record<string, string>
): Promise<ClientPartner> {
  const callable = functions.httpsCallable(SET_PARTNER_CREDENTIALS_CALLABLE);
  const result = await callable({ clientId, partnerId, credentials });
  const partner = (result.data as { partner?: ClientPartner } | null)?.partner;
  if (!partner) {
    throw new Error("No partner returned.");
  }
  return partner;
}

/**
 * Creates an external system for a client (#2610084, merge 3): its sign-in account, record and link, all
 * or nothing. The password comes back once and is stored nowhere.
 *
 * @returns the new system's page and its password. Rejects when Cloud refuses (`details.reason`).
 */
export async function createClientPartner(
  clientId: string,
  request: PartnerCreateRequest
): Promise<{ partner: ClientPartner; password: string }> {
  const callable = functions.httpsCallable(CREATE_PARTNER_CALLABLE);
  const result = await callable({ clientId, ...request });
  const data = (result.data ?? {}) as { partner?: ClientPartner; password?: string };
  if (!data.partner || !data.password) {
    throw new Error("No external system returned.");
  }
  return { partner: data.partner, password: data.password };
}

/**
 * A new password for an external system's own sign-in (#2610084, merge 3); the old one stops working.
 *
 * @returns the new password, once.
 */
export async function resetClientPartnerPassword(clientId: string, partnerId: string): Promise<string> {
  const callable = functions.httpsCallable(RESET_PARTNER_PASSWORD_CALLABLE);
  const result = await callable({ clientId, partnerId });
  const password = (result.data as { password?: string } | null)?.password;
  if (!password) {
    throw new Error("No password returned.");
  }
  return password;
}

/**
 * Links a member of the owner's group to an external system, or removes the link (#2610084, merge 3).
 *
 * @param clientId - the system's owner, whose page this is.
 * @param memberClientId - the member.
 * @param linked - link (true) or remove the link (false).
 * @returns the page after the change.
 */
export async function setClientPartnerLink(
  clientId: string,
  partnerId: string,
  memberClientId: string,
  linked: boolean
): Promise<ClientPartner> {
  const callable = functions.httpsCallable(SET_PARTNER_LINK_CALLABLE);
  const result = await callable({ clientId, partnerId, memberClientId, linked });
  const partner = (result.data as { partner?: ClientPartner } | null)?.partner;
  if (!partner) {
    throw new Error("No external system returned.");
  }
  return partner;
}

/** Every external system for the "Externe Systeme" list (#2610084, merge 3), by name. */
export async function listExternalSystems(): Promise<ExternalSystemRow[]> {
  const callable = functions.httpsCallable(LIST_EXTERNAL_SYSTEMS_CALLABLE);
  const result = await callable({});
  const systems = (result.data as { systems?: ExternalSystemRow[] } | null)?.systems ?? [];
  rememberAdminRead(LIST_EXTERNAL_SYSTEMS_CALLABLE, null, systems);
  return systems;
}

/** The last answer of {@link listExternalSystems} in this tab, or null (`AdminReadCache.ts`). */
export function cachedExternalSystems(): ExternalSystemRow[] | null {
  return recallAdminRead(LIST_EXTERNAL_SYSTEMS_CALLABLE, null);
}

/**
 * A system's integration packages (#2610090), newest first, with what the create dialog offers.
 *
 * @returns the list; `available` false for an EasyTec system. Rejects when Cloud refuses (`details.reason`).
 */
export async function listClientPartnerPackages(clientId: string, partnerId: string): Promise<ClientPartnerPackageList> {
  const callable = functions.httpsCallable(LIST_PARTNER_PACKAGES_CALLABLE);
  const result = await callable({ clientId, partnerId });
  const data = (result.data ?? {}) as Partial<ClientPartnerPackageList>;
  return {
    available: data.available === true,
    packages: data.packages ?? [],
    groups: data.groups ?? [],
    startGroups: data.startGroups ?? [],
    apiVersion: data.apiVersion ?? "",
  };
}

/**
 * Creates an integration package (#2610090): IRES Cloud builds the three files from the current API
 * description and the system's settings, and records who, when, the version and the recipient.
 *
 * @returns the new package. Rejects when Cloud refuses (`details.reason`).
 */
export async function createClientPartnerPackage(
  clientId: string,
  partnerId: string,
  groups: PartnerEndpointGroup[],
  recipient: PartnerPackageRecipient
): Promise<ClientPartnerPackage> {
  const callable = functions.httpsCallable(CREATE_PARTNER_PACKAGE_CALLABLE);
  const result = await callable({ clientId, partnerId, groups, recipient });
  const created = (result.data as { package?: ClientPartnerPackage } | null)?.package;
  if (!created) {
    throw new Error("No package returned.");
  }
  return created;
}

/**
 * A short-lived link to one file of a package (#2610090), downloaded under the name the partner receives.
 *
 * @returns the signed URL. Rejects when Cloud refuses (`details.reason`).
 */
export async function getClientPartnerPackageFileUrl(clientId: string, packageId: string, file: PartnerPackageFile): Promise<string> {
  const callable = functions.httpsCallable(GET_PARTNER_PACKAGE_FILE_CALLABLE);
  const result = await callable({ clientId, packageId, file });
  const url = (result.data as { url?: unknown } | null)?.url;
  if (typeof url !== "string" || url === "") {
    throw new Error("No file link returned.");
  }
  return url;
}
